<script setup>
import { reactive, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import {
  required,
  minLength,
  email as emailValidator,
} from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  displayName: { type: String, default: '' },
  emailEnabled: { type: Boolean, default: false },
});
const emit = defineEmits(['updateUser']);
const { t } = useI18n();
const form = reactive({
  userName: props.name,
  userDisplayName: props.displayName,
  userEmail: props.email,
});
const { userName, userDisplayName, userEmail } = toRefs(form);
const v$ = useVuelidate(
  {
    userName: { required, minLength: minLength(1) },
    userDisplayName: {},
    userEmail: { required, email: emailValidator },
  },
  form
);
watch(
  () => props.name,
  value => {
    form.userName = value;
  }
);
watch(
  () => props.displayName,
  value => {
    form.userDisplayName = value;
  }
);
watch(
  () => props.email,
  value => {
    form.userEmail = value;
  }
);
const updateUser = async () => {
  if (!(await v$.value.$validate())) {
    useAlert(t('PROFILE_SETTINGS.FORM.ERROR'));
    return;
  }
  emit('updateUser', {
    name: form.userName,
    displayName: form.userDisplayName,
    email: form.userEmail,
  });
};
</script>

<template>
  <form class="grid w-full gap-4 md:grid-cols-2" @submit.prevent="updateUser">
    <woot-input
      v-model="userName"
      :class="{ error: v$.userName.$error }"
      :label="t('PROFILE_SETTINGS.FORM.NAME.LABEL')"
      :placeholder="t('PROFILE_SETTINGS.FORM.NAME.PLACEHOLDER')"
      :error="`${
        v$.userName.$error ? t('PROFILE_SETTINGS.FORM.NAME.ERROR') : ''
      }`"
      @input="v$.userName.$touch"
      @blur="v$.userName.$touch"
    />
    <woot-input
      v-model="userDisplayName"
      :class="{ error: v$.userDisplayName.$error }"
      :label="t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.LABEL')"
      :placeholder="t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.PLACEHOLDER')"
      :help-text="t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.HELP_TEXT')"
      :error="`${
        v$.userDisplayName.$error
          ? t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.ERROR')
          : ''
      }`"
      @input="v$.userDisplayName.$touch"
      @blur="v$.userDisplayName.$touch"
    />
    <woot-input
      v-if="emailEnabled"
      v-model="userEmail"
      class="md:col-span-2"
      :class="{ error: v$.userEmail.$error }"
      :label="t('PROFILE_SETTINGS.FORM.EMAIL.LABEL')"
      :placeholder="t('PROFILE_SETTINGS.FORM.EMAIL.PLACEHOLDER')"
      :error="`${
        v$.userEmail.$error ? t('PROFILE_SETTINGS.FORM.EMAIL.ERROR') : ''
      }`"
      @input="v$.userEmail.$touch"
      @blur="v$.userEmail.$touch"
    />
    <div class="md:col-span-2">
      <NextButton type="submit" :label="t('PROFILE_SETTINGS.BTN_TEXT')" />
    </div>
  </form>
</template>
